"use client";

import React from "react";
import Image, { type StaticImageData } from "next/image";
import PrimaryBtn from "@/components/PrimaryBtn";
import { Icons } from "@assets/assets";
import { useTheme } from "@/HomeFeatures/Context/ThemeContext";

interface ServicesProps {
  title1: string;
  title2: React.ReactNode;
  description: React.ReactNode;
  servicesArray: ServiceItem[];
}
type ServiceItem = {
  icon: string | StaticImageData;
  title: string;
  description: string;
};

export default function AiServices({
  title1,
  title2,
  description,
  servicesArray,
}: ServicesProps) {
  const { handleOpenQuote } = useTheme();
  return (
    <section className="relative bg-[#d9e2ed]">
      <div className="w-[calc(100%_-_10%)] mx-auto max-w-[2500px] flex flex-col gap-[60px] lg:py-[150px] py-[70px]">
        {/* Header row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[30px] items-center">
          <div className="flex flex-col gap-[15px]">
            <h5 className="text-[length:var(--primarybtn)] font-medium uppercase tracking-tight text-[#00040b]">
              {title1}
            </h5>
            <h2 className="clash-font font-medium leading-[120%] text-[#00040b] text-[length:var(--section-title)]">
              {title2}
            </h2>
          </div>
          <p className="text-[#00040b]/70 text-[length:var(--hero-para)] leading-[160%]">
            {description}
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {servicesArray?.map((item) => (
            <div
              key={item.title}
              className="bg-white border border-[#ecf0f6] p-8 lg:p-[42px] flex flex-col gap-[60px]"
            >
              <div>
                <Image
                  src={item.icon}
                  alt={item.title}
                  width={66}
                  height={66}
                  sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
                  priority
                  fetchPriority="high"
                />
              </div>
              <div className="flex flex-col gap-5">
                <h3 className="text-[22px] lg:text-[clamp(0.9375rem,0.4664rem+0.9815vw,2rem)] clash-font font-medium leading-[120%] text-[#00040b]">
                  {item.title}
                </h3>
                <p
                  className="text-[18px] lg:text-[20px] leading-[140%] text-[#636364]"
                  dangerouslySetInnerHTML={{ __html: item.description ?? "" }}
                />
              </div>
            </div>
          ))}

          {/* CTA card */}
          <div className="relative overflow-hidden bg-[#225bd4] p-8 lg:p-[42px] flex items-center justify-center">
            {/* Content */}
            <div className="w-full max-w-[448px] mx-auto text-center flex flex-col items-center gap-5">
              <h3 className="text-white text-[28px] sm:text-[32px] md:text-[36px] lg:text-[38px] clash-font font-semibold leading-[120%]">
                Looking for Secure and Flexible AI Development?
              </h3>
              <p className="text-white text-[16px] clash-font font-medium">
                Partner With Us to Develop App & Web Solutions.
              </p>
              <div className="mt-2">
                <PrimaryBtn
                  text="Let's Connect"
                  iconSrc={Icons?.chevronBlue}
                  dark={true}
                  onClick={() => {
                    handleOpenQuote();
                  }}
                />
              </div>
            </div>

            {/* Decorative blurs (subtle) */}
            <div className="pointer-events-none absolute -top-40 left-16 w-[320px] h-[320px] rounded-full bg-white/10 blur-[120px]" />
            <div className="pointer-events-none absolute -bottom-40 -left-16 w-[320px] h-[320px] rounded-full bg-white/10 blur-[120px]" />
          </div>
        </div>
      </div>
    </section>
  );
}
